import { Fragment, useEffect, useRef, useState } from "react";
import GeneralBtn from "../../../../components/GeneralBtn";
import { Box } from "../../../../components/MuiGenerals";
import { useTranslation } from "../../../../hooks";
import { genField } from "../../../../utils/HelperComp";
import { customSx } from "../../../../utils/styling";
import {
  translatePrefix,
  translateKeyObj as TK,
  initParams,
  IFilterFields,
  initFilterFields,
} from "../config";
import { useReduxContext, setMerchantTabParams } from "../reduxContext";
import { ChainSingleSelection } from "../../../../components/ChainSelection";
import { selectCrossMerchant } from "../../../../reducer/crossMerchantSlice";
import { AssetMultiSelection } from "../../../../components/AssetSelection";
import { useAssets, useChains } from "../../../../components/FetchConfig";
import {
  NodeTypesEnumMultiSelection,
  NodeTypesEnumSingleSelection,
} from "../../../../components/GeneralSelection";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectProfile } from "../../../../reducer/profileSlice";
import MpTextField from "../../../../components/MpTextField";
import LPMerchantSingleSelection from "../../../../components/Common/CrossMerchant/LPMerchantSingleSelection";

interface RenderField {
  [key: string]: string;
}

const ViewFilter = () => {
  const [_, dispatch] = useReduxContext();

  const [fields, setFields] = useState<IFilterFields>(initFilterFields);

  const merchantFromInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation(translatePrefix);

  const { selectChainByPieces } = useChains();

  const { selectAssetNamesByChain } = useAssets();
  const { merchantId } = useAppSelector(selectProfile);

  const allAssetsFrom = selectAssetNamesByChain({
    chain_name: fields.fromChainName,
  });

  const allAssetsTo = selectAssetNamesByChain({
    chain_name: fields.toChainName,
  });

  const { merchantMapping, liquidityProvidersMapping } =
    useAppSelector(selectCrossMerchant);

  const getParams = () => {
    const { fromChainName } = fields;

    const { chain_id: fromChainId, chain_type: fromChainType } =
      selectChainByPieces({ chain_name: fromChainName });

    return {
      ...initParams,
      ...fields,
      fromChainId,
      fromChainType,
    };
  };

  const apiParams = getParams();

  const onSearch = async () => {
    dispatch(setMerchantTabParams(apiParams));
  };

  const onReset = () => {
    setFields(initFilterFields);
  };

  useEffect(() => {
    if (!merchantId || !merchantFromInputRef.current) {
      return;
    }

    merchantFromInputRef.current.value = String(
      merchantMapping[merchantId] || ""
    );
  }, [merchantMapping]);

  const filterFields: RenderField = genField({ t }, [
    [
      TK.merchantFrom,
      <NodeTypesEnumMultiSelection
        label={t(TK.phMerchantFrom)}
        onChange={(merchantIds) =>
          setFields({
            ...fields,
            fromMerchantIds: merchantIds,
          })
        }
        value={fields.fromMerchantIds}
        enumData={merchantMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      TK.merchantTo,
      <NodeTypesEnumMultiSelection
        label={t(TK.phMerchantTo)}
        onChange={(toMerchantIds) =>
          setFields({
            ...fields,
            toMerchantIds: toMerchantIds,
          })
        }
        value={fields.toMerchantIds}
        enumData={merchantMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      TK.chainNameFrom,
      <ChainSingleSelection
        label={t(TK.phChainNameFrom)}
        setChoice={(chainName) =>
          setFields({
            ...fields,
            fromChainName: chainName,
            fromAssetNames: [],
          })
        }
        choice={fields.fromChainName}
      />,
    ],
    [
      TK.chainNameTo,
      <ChainSingleSelection
        label={t(TK.phChainNameTo)}
        setChoice={(chainName) =>
          setFields({
            ...fields,
            toChainName: chainName,
            toAssetNames: [],
          })
        }
        choice={fields.toChainName}
      />,
    ],
    [
      TK.currencyFrom,
      <AssetMultiSelection
        label={t(TK.phCurrencyFrom)}
        setChoices={(assetNames) =>
          setFields({
            ...fields,
            fromAssetNames: assetNames,
          })
        }
        choices={fields.fromAssetNames}
        allItems={allAssetsFrom}
      />,
    ],
    [
      TK.currencyTo,
      <AssetMultiSelection
        label={t(TK.phCurrencyTo)}
        setChoices={(assetNames) =>
          setFields({
            ...fields,
            toAssetNames: assetNames,
          })
        }
        choices={fields.toAssetNames}
        allItems={allAssetsTo}
      />,
    ],
    [
      TK.merchantLp,
      <LPMerchantSingleSelection
        value={fields.liquidityProviderMerchantId || ""}
        onChange={(e) =>
          setFields({
            ...fields,
            liquidityProviderMerchantId: e.target.value,
          })
        }
        onClearSelect={() =>
          setFields({
            ...fields,
            liquidityProviderMerchantId: "",
          })
        }
      />,
    ],
    [
      TK.liquidityProvider,
      <NodeTypesEnumMultiSelection
        label={t(TK.phLiquidityProvider)}
        onChange={(liquidityProviders) =>
          setFields({
            ...fields,
            liquidityProviders: liquidityProviders,
          })
        }
        value={fields.liquidityProviders}
        enumData={liquidityProvidersMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
  ]);

  const renderFields = () =>
    Object.keys(filterFields).map((key, index) => (
      <Fragment key={index}>{filterFields[key]}</Fragment>
    ));

  return (
    <>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {renderFields()}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
};

export default ViewFilter;
